import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  Box
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import MAvatar from '../../components/@material-extend/MAvatar';
import createAvatar from '../../common/createAvatar';
import { PATH_DASHBOARD, PATH_ORG } from '../../routes/paths';
import useLocales from '../../hooks/useLocales';
import type { RootState } from '../../redux/store';
import { useDispatch, useSelector } from '../../redux/store';
import { slice } from '../../features/organization-context/slice';
import { setActiveOrgGuid } from '../../common/activeOrgStorage';
import { isOrgAdmin } from '../../features/organization-context/types';
import CreateOrganizationDialog from './CreateOrganizationDialog';

interface OrgSwitcherPopoverProps {
  collapsed?: boolean;
}

export default function OrgSwitcherPopover({ collapsed = false }: OrgSwitcherPopoverProps) {
  const { translate } = useLocales();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { memberships, activeOrgGuid } = useSelector(
    (state: RootState) => state.organizationContext
  );
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [createSpaceOpen, setCreateSpaceOpen] = useState(false);
  const open = Boolean(anchor);

  const handleClose = () => setAnchor(null);

  const activeOrg = memberships.find((m) => m.organizationGuid === activeOrgGuid);
  const currentLabel = activeOrg ? activeOrg.name : translate('org_switcher.personal');
  const activeOrgAvatar = activeOrg ? createAvatar(activeOrg.name) : null;

  const renderIdentityAvatar = (size: number) =>
    activeOrgAvatar ? (
      <MAvatar
        color={activeOrgAvatar.color}
        sx={{ width: size, height: size, fontSize: size * 0.55, flexShrink: 0 }}
      >
        {activeOrgAvatar.name}
      </MAvatar>
    ) : (
      <MAvatar
        sx={{
          width: size,
          height: size,
          bgcolor: 'grey.300',
          color: 'text.secondary',
          flexShrink: 0
        }}
      >
        <PersonOutlineIcon sx={{ fontSize: size * 0.65 }} />
      </MAvatar>
    );

  // Switching into a space is currently an org-admin action — plain members still
  // see their org's circles via Personal context's cross-org aggregation, they just
  // don't get a dedicated space to switch into.
  const switchableMemberships = memberships.filter((m) => isOrgAdmin(m.role));

  const switchTo = (orgGuid: string | null) => {
    setActiveOrgGuid(orgGuid);
    dispatch(slice.actions.setActiveOrg(orgGuid));
  };

  const handleSelectPersonal = () => {
    handleClose();
    switchTo(null);
    navigate(PATH_DASHBOARD.overview.root);
  };

  const handleSelectOrg = (organizationGuid: string) => {
    handleClose();
    switchTo(organizationGuid);
    navigate(PATH_ORG.overview);
  };

  const handleCreateSpace = () => {
    handleClose();
    setCreateSpaceOpen(true);
  };

  return (
    <>
      {collapsed ? (
        <Button
          onClick={(e) => setAnchor(e.currentTarget)}
          color="inherit"
          aria-label={currentLabel}
          sx={{
            minWidth: 0,
            px: 1,
            py: 1,
            borderRadius: 1.5,
            border: '1px solid',
            bgcolor: open ? 'action.selected' : 'action.hover',
            borderColor: open ? 'divider' : 'transparent',
            '&:hover': {
              bgcolor: 'action.selected',
              border: (t) => `1px solid ${t.palette.divider}`
            }
          }}
        >
          {renderIdentityAvatar(24)}
        </Button>
      ) : (
        <Button
          fullWidth
          onClick={(e) => setAnchor(e.currentTarget)}
          color="inherit"
          sx={{
            justifyContent: 'flex-start',
            gap: 1,
            px: 1,
            py: 0.75,
            borderRadius: 1.5,
            border: '1px solid',
            bgcolor: open ? 'action.selected' : 'action.hover',
            borderColor: open ? 'divider' : 'transparent',
            '&:hover': {
              bgcolor: 'action.selected',
              border: (t) => `1px solid ${t.palette.divider}`
            }
          }}
        >
          {renderIdentityAvatar(24)}
          <Typography variant="subtitle2" sx={{ flexGrow: 1, textAlign: 'left' }} noWrap>
            {currentLabel}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 16,
              color: 'text.secondary',
              flexShrink: 0,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: (t) =>
                t.transitions.create('transform', { duration: t.transitions.duration.shortest })
            }}
          />
        </Button>
      )}

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 240 } } }}
      >
        <MenuItem onClick={handleSelectPersonal}>
          <ListItemIcon>
            <MAvatar sx={{ width: 22, height: 22, bgcolor: 'grey.300', color: 'text.secondary' }}>
              <PersonOutlineIcon sx={{ fontSize: 14 }} />
            </MAvatar>
          </ListItemIcon>
          <ListItemText
            primary={translate('org_switcher.personal')}
            slotProps={{ primary: { variant: 'body2' } }}
          />
          {!activeOrgGuid && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
              <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            </Box>
          )}
        </MenuItem>

        {switchableMemberships.map((membership) => {
          const memberAvatar = createAvatar(membership.name);
          return (
            <MenuItem
              key={membership.organizationGuid}
              onClick={() => handleSelectOrg(membership.organizationGuid)}
            >
              <ListItemIcon>
                <MAvatar color={memberAvatar.color} sx={{ width: 22, height: 22, fontSize: 12 }}>
                  {memberAvatar.name}
                </MAvatar>
              </ListItemIcon>
              <ListItemText
                primary={membership.name}
                slotProps={{ primary: { variant: 'body2' } }}
              />
              {activeOrgGuid && activeOrgGuid === membership.organizationGuid && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                  <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                </Box>
              )}
            </MenuItem>
          );
        })}

        <Divider sx={{ my: 0.5 }} />

        <MenuItem onClick={handleCreateSpace}>
          <ListItemIcon>
            <AddOutlinedIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary={translate('org_switcher.create_organization')}
            slotProps={{ primary: { variant: 'body2' } }}
          />
        </MenuItem>
      </Menu>

      <CreateOrganizationDialog open={createSpaceOpen} onClose={() => setCreateSpaceOpen(false)} />
    </>
  );
}
